import React, { useState } from 'react';
import { Global } from '@emotion/react';
import t from 'react-translate';
import { useAppDispatch } from 'redux/store';
import { useSelector } from 'react-redux';
import { wrapThunkAction } from 'redux/utils';

import {
  patchSuggestedComponentSet,
  resetAiSuggestComponentsState, resetDestinationLecturePage,
  setAddedSuggestedComponentSetIds,
  setAddingSuggestedComponentSetId,
  setLeftPanelAiMode,
} from 'redux/actions/lecture-pages';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { addNewComponent } from 'redux/actions/lecture-components';

import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { RootState } from 'redux/schemas';
import { ComponentType, LectureComponent } from 'redux/schemas/models/lecture-component';
import { NovoAIContentType, NovoAIItemType } from 'redux/schemas/models/lecture-page';

import { getCourseAliases } from 'redux/selectors/course';

import NvGradientBorderButton from 'shared/components/button/nv-gradient-border-button';
import BallAnimation from 'timelines/components/novo_ai_course_creation/ball-animation';
import LecturePagePreviewModal, { LecturePagePreviewContext, PreviewSource } from 'lecture_pages/components/lecture-page-preview-modal';
import { useLecturePageParams } from 'lecture_pages/hooks/lecture-routing';
import { hideAddUI } from 'lecture_pages/templates/components/nv-add-component';
import SuggestActivitiesModalContent from './suggest-activities-modal-content';
import GradientAiIcon from '../shared/gradient-ai-icon';
import useAiSuggestComponents from './hooks/use-ai-suggest-components';

import { config } from '@config/config.json';

import { lhsCommonStyles, lhsStyles } from './styles';
import AiModal from '../shared/ai-modal';

const SuggestActivities = () => {
  const [showQuestionModal, setShowQuestionModal] = useState<boolean>(false);
  const [showLessonPreview, setShowLessonPreview] = useState<boolean>(false);

  const { activitySets, addingComponentSetId } = useSelector((state: RootState) => state.app.aiSuggestComponent);
  const aliases = useSelector((state) => getCourseAliases(state));
  const { componentTextMapping, buildComponentCreationPayload } = useAiSuggestComponents();

  const dispatch = useAppDispatch();
  const params = useLecturePageParams();

  const openGenerateActivitiesModal = () => {
    dispatch(setLeftPanelAiMode({
      itemType: NovoAIItemType.SUGGEST_ACTIVITIES,
      content: {
        id: params.lecturePageId,
        type: NovoAIContentType.LESSON,
      },
    }));
    setShowQuestionModal(true);
  };

  const onClosePreviewModal = (resetAdding = false) => {
    if (resetAdding) {
      dispatch(setAddingSuggestedComponentSetId(null));
    }
    setShowLessonPreview(false);
    dispatch(resetDestinationLecturePage());
    setShowQuestionModal(true);
  };

  const openCurrentLessonPreview = () => {
    setShowQuestionModal(false);
    setShowLessonPreview(true);
  };

  const closeModal = () => {
    setShowQuestionModal(false);
    dispatch(resetAiSuggestComponentsState());
    hideAddUI(dispatch);
  };

  const onAddLesson = (index: number) => {
    onClosePreviewModal();
    let { activity } = activitySets[addingComponentSetId];

    const requests = activitySets[addingComponentSetId].secondaryActivities.map((lc, i) => wrapThunkAction(
      dispatch(addNewComponent(buildComponentCreationPayload(
        lc,
        index + i,
        NovoAIItemType.SUGGEST_ACTIVITIES,
      ))),
    ).then((response) => {
      const component = response.payload as LectureComponent;

      // If the selected activity is a public feedback for a new ai suggested Exercise,
      // we need to link the exerciseId after it's created.
      if (component.type === ComponentType.EXERCISE) {
        activity = {
          ...activity,
          peerEvaluation: {
            ...(activity as any).peerEvaluation,
            exerciseId: component.exercise.id,
          },
        };

        if (activitySets[addingComponentSetId].relatedSetId) {
          dispatch(setAddedSuggestedComponentSetIds(activitySets[addingComponentSetId].relatedSetId));
        }
      }
    }).catch(() => {
      dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.OOPS(),
        message: t.FORM.ERROR_SOMETHING_WRONG(),
      }));
    }));

    Promise.all(requests).finally(() => {
      dispatch(addNewComponent(
        buildComponentCreationPayload(
          activity,
          index + activitySets[addingComponentSetId].secondaryActivities.length,
          NovoAIItemType.SUGGEST_ACTIVITIES,
        ),
      )).then((response) => {
        const component = response.payload as LectureComponent;

        /**
         * If the suggested exercise has public feedback associated with it, we
         * need to link the exerciseId and clear the secondaryActivities after
         * it’s created to prevent multiple exercise creations and ensure the
         * link is maintained correctly when creating the feedback later.
         */
        if (
          component.type === ComponentType.EXERCISE
          && activitySets[addingComponentSetId].relatedSetId
        ) {
          const relatedActivitySet = activitySets[activitySets[addingComponentSetId].relatedSetId];
          dispatch(patchSuggestedComponentSet({
            ...relatedActivitySet,
            secondaryActivities: [],
            activity: {
              ...relatedActivitySet.activity,
              peerEvaluation: {
                ...(relatedActivitySet.activity as any).peerEvaluation,
                exerciseId: component.exercise.id,
              },
            },
          }));
        }

        let alertMessage = t.LECTURE_PAGES.LHS.NOVO_AI.SUGGEST_ACTIVITIES.ADDED_ALERT({
          type: componentTextMapping[activity.type],
          lectureAlias: aliases.lectureAliases.lectureAlias,
        });

        if (
          component.type === ComponentType.PUBLIC_PEER_EVALUATION
          && activitySets[addingComponentSetId].secondaryActivities?.[0]?.type === ComponentType.EXERCISE
        ) {
          alertMessage = t.LECTURE_PAGES.LHS.NOVO_AI.SUGGEST_ACTIVITIES.MULTIPLE_ADDED_ALERT({
            primaryType: componentTextMapping[ComponentType.PUBLIC_PEER_EVALUATION],
            relatedType: componentTextMapping[ComponentType.EXERCISE],
            lectureAlias: aliases.lectureAliases.lectureAlias,
          });
        }

        dispatch(addAlertMessage({
          type: AlertMessageType.SUCCESS,
          header: t.FORM.SUCCESS(),
          message: alertMessage,
        }));

        dispatch(setAddedSuggestedComponentSetIds(addingComponentSetId));
      });
    });
  };

  return (
    <React.Fragment>
      <Global
        styles={lhsCommonStyles}
      />
      <div
        className='generate-box d-flex flex-column pt-4 pb-5 mt-2'
        css={lhsStyles}
      >
        <BallAnimation />
        <div className='text-center px-4'>
          <div className='text-regular font-weight-bolder mb-2'>
            {t.LECTURE_PAGES.LHS.NOVO_AI.SUGGEST_ACTIVITIES.CONTENT.HEADER()}
          </div>
          <div>
            {t.LECTURE_PAGES.LHS.NOVO_AI.SUGGEST_ACTIVITIES.CONTENT.DESCRIPTION()}
          </div>
        </div>

        <NvGradientBorderButton
          onClick={openGenerateActivitiesModal}
          className='mx-5 py-2 align-self-center'
          dataQa={config.pendo.novoAi.suggestActivities.openModal}
        >
          <GradientAiIcon icon='gen-ai' size='small' className='pr-2' />
          <span className='text-regular font-weight-bolder text-nowrap'>
            {t.LECTURE_PAGES.LHS.NOVO_AI.SUGGEST_ACTIVITIES.BUTTON_TEXT()}
          </span>
        </NvGradientBorderButton>
        <AiModal
          show={showQuestionModal}
          onClose={closeModal}
          closeDataQa={config.pendo.novoAi.suggestActivities.closeModal}
          body={(
            <SuggestActivitiesModalContent
              closeModal={closeModal}
              onAddLesson={openCurrentLessonPreview}
            />
          )}
        />
        <LecturePagePreviewContext.Provider value={{
          lecturePageId: params.lecturePageId,
          source: PreviewSource.AI_ADD,
          onClosePreviewModal,
          onAction: onAddLesson,
        }}
        >
          <LecturePagePreviewModal
            show={showLessonPreview}
            onClose={() => onClosePreviewModal(true)}
            header={t.LECTURE_PAGES.LHS.NOVO_AI.SUGGEST_ACTIVITIES.PREVIEW_MODAL_HEADER()}
          />
        </LecturePagePreviewContext.Provider>
      </div>
    </React.Fragment>
  );
};

export default SuggestActivities;
